// Pure WHT calculation functions — no Firestore/React imports so these are
// trivially unit-testable. Every formula here is a direct extraction of what
// already runs today in src/utils/cortevaInvoiceRegistry.js (Model 1) and
// src/components/IILInvoice.js (Model 2) — this module does not change any
// existing client's numbers, it only makes the formulas shared and testable.

export const WHT_MODELS = Object.freeze({
  SERVICE_CHARGES_PLUS_GST: "SERVICE_CHARGES_PLUS_GST",
  GROSS_INVOICE_WITH_RECOVERY: "GROSS_INVOICE_WITH_RECOVERY"
});

export function round(value) {
  return Math.round(Number(value || 0));
}

// Model 1 (e.g. Corteva today): WHT = rate x (service charges + GST on those
// service charges). Matches cortevaInvoiceRegistry.js:29 exactly.
export function calculateModel1Wht({ serviceCharges, gstAmount, whtRate }) {
  return round((Number(serviceCharges || 0) + Number(gstAmount || 0)) * Number(whtRate || 0));
}

// Model 2 (e.g. IIL today): WHT = rate x full gross invoice (incl. GST).
// Matches IILInvoice.js:1479 (approveInvoiceNumber) exactly.
export function calculateModel2Wht({ grossAmount, whtRate }) {
  return round(Number(grossAmount || 0) * Number(whtRate || 0));
}

// GST withheld by the client — identical shape in both models today
// (Corteva and IIL both use gstAmount x 0.20). Matches both approval
// functions' `gstWithheld` calc.
export function calculateGstWithheld({ gstAmount, gstWithholdingRate }) {
  return round(Number(gstAmount || 0) * Number(gstWithholdingRate || 0));
}

// expectedReceipt = grossAmount - WHT - GST withheld - other approved deductions.
// Matches both approval functions' `expectedReceipt` calc (no "other deductions"
// exist yet in Stage 1 — that's a Stage 2 concept once receipts/adjustments land).
export function calculateExpectedReceipt({ grossAmount, whtAmount, gstWithheldAmount, otherDeductions = 0 }) {
  return Number(grossAmount || 0) - Number(whtAmount || 0) - Number(gstWithheldAmount || 0) - Number(otherDeductions || 0);
}

// Gross-up "recovery add-back" used by Model 2 clients whose invoice amount
// itself is built to survive the client's WHT deduction (IIL today: rate =
// (0.07*1.16)/(1-0.07*1.16) ~= 8.8376%, applied to (totalCost+service)).
// This is the amount ADDED to the invoice before GST, not the WHT deduction
// recorded at approval — the two are related but distinct, per IILInvoice.js:688
// vs :1479. Both numbers get stored on the snapshot rather than merged into one.
export function calculateGrossUpRecoveryAddBack({ recoveryBase, recoveryRate }) {
  return round(Number(recoveryBase || 0) * Number(recoveryRate || 0));
}

// Compares the WHT that will actually be deducted at approval time (per the
// active tax profile's flat rate on gross) against the recovery add-back
// already embedded in the invoice when it was built (per the profile's
// gross-up rate). These are DESIGNED to match — the gross-up rate is a fixed
// point specifically constructed so that a given WHT rate produces a
// recovery line item exactly equal to the WHT deduction that rate will
// produce (see IIL: recoveryRate = (whtRate*1.16)/(1-whtRate*1.16), verified
// algebraically to satisfy recoveryAddBack == whtRate*grossAmount exactly).
// A nonzero variance means the two rates have drifted apart — e.g. the WHT
// rate changed after the invoice's line items were built, not that ALM is
// failing to recover its payroll cost (that quantity is a different one:
// totalCost+service vs. the grossed-up invoice total, which will never be
// equal by construction, since GST cash more than offsets a 7% WHT bite).
export function calculateRecoveryVariance({ actualWhtDeducted, recoveryAddBack }) {
  return round(Number(actualWhtDeducted || 0) - Number(recoveryAddBack || 0));
}

// Single entry point used by every adapter/approval function so a client's
// WHT model only has to be looked up once (from its active client_tax_profiles
// doc) rather than branched on in every invoice page.
export function calculateWht(model, params) {
  if (model === WHT_MODELS.SERVICE_CHARGES_PLUS_GST) {
    return calculateModel1Wht(params);
  }
  if (model === WHT_MODELS.GROSS_INVOICE_WITH_RECOVERY) {
    return calculateModel2Wht(params);
  }
  // No active tax profile yet (e.g. CBRE/CPBM before finance reviews a rate) —
  // WHT is 0 rather than guessed, per the plan's explicit DRAFT-profile fallback.
  return 0;
}
